<template>
    <div>
        <!-- List of rewards -->
        <div v-if="editingRewardIdx === null">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">抽選条件設定</h5>
                <button type="button" class="btn btn-primary btn-sm" @click="openAddReward"><i class="ri-add-line"></i> 新規作成</button>
            </div>
            <table class="table table-bordered">
                <thead>
                    <tr>
                        <th>当選テキスト</th>
                        <th>当選数</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(reward, idx) in rewards" :key="'reward-row-' + idx">
                        <td>{{ reward.text || '未入力' }}</td>
                        <td>{{ reward.quantity }}</td>
                        <td>
                            <button class="btn btn-sm btn-info mr-1" @click="editReward(idx)">編集</button>
                            <button class="btn btn-sm btn-danger" @click="deleteReward(idx)">削除</button>
                        </td>
                    </tr>
                    <tr v-if="!rewards.length">
                        <td colspan="3" class="text-center text-muted">データがありません。</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Edit single reward -->
        <div v-else>
            <div class="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                <h5 class="mb-0">{{ isAddingNew ? '新規作成' : '当選設定' }}</h5>
            </div>

            <div class="form-group row">
                <label class="col-sm-3 col-form-label">当選テキスト <span class="text-danger">*</span></label>
                <div class="col-sm-9">
                    <input
                        v-model="currentReward.text"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.text }"
                        placeholder="A賞"
                        @input="validateText"
                    />
                    <div v-if="errors.text" class="invalid-feedback" style="display: block">{{ errors.text }}</div>
                </div>
            </div>
            <div class="form-group row">
                <label class="col-sm-3 col-form-label">当選数 <span class="text-danger">*</span></label>
                <div class="col-sm-9">
                    <input
                        v-model.number="currentReward.quantity"
                        type="number"
                        min="1"
                        class="form-control"
                        :class="{ 'is-invalid': errors.quantity }"
                        @keypress="onQuantityKeypress"
                        @input="onQuantityInput"
                    />
                    <div v-if="errors.quantity" class="invalid-feedback" style="display: block">{{ errors.quantity }}</div>
                </div>
            </div>

            <hr />
            <!-- Condition filter (TableFilterBlock) -->
            <h5 class="mb-3">抽選条件設定</h5>
            <!-- Using key to force re-render so state is somewhat fresh for new rewards -->
            <TableFilterBlock
                ref="tableFilter"
                :key="'filter-' + editingRewardIdx"
                :blockList="listBlockForm"
                @onQuery="onConditionQuery"
                :hideActionButtons="true"
            />

            <hr />
            <!-- Pre-draw duplicate -->
            <h5 class="mb-2">抽選前重複確認</h5>
            <div v-for="(field, idx) in currentReward.preDrawFields" :key="'pre-' + idx" class="d-flex align-items-center mb-2">
                <Multiselect
                    :id="'pre-dup-' + idx"
                    :value.sync="currentReward.preDrawFields[idx]"
                    :options="listBlockForm"
                    :config="{ trackBy: 'id', label: 'block_name', isRemove: true }"
                    style="min-width: 220px"
                    class="w-50"
                />
                <button type="button" class="btn btn-sm btn-success ml-2" @click="currentReward.preDrawFields.push(null)">
                    <i class="ri-add-line"></i>
                </button>
                <button type="button" class="btn btn-sm btn-danger ml-1" @click="currentReward.preDrawFields.splice(idx, 1)">
                    <i class="ri-subtract-line"></i>
                </button>
            </div>
            <button
                type="button"
                class="btn btn-sm btn-success mb-2"
                @click="currentReward.preDrawFields.push(null)"
                v-if="currentReward.preDrawFields.length === 0"
            >
                <i class="ri-add-line"></i>
            </button>
            <br v-if="currentReward.preDrawFields.length === 0" />
            <div class="text-center mt-3">
                <button type="button" class="btn btn-outline-secondary btn-common mr-3" @click="clearPreview">クリア</button>
                <button type="button" class="btn btn-primary btn-common" :disabled="currentReward.isLoadingPreview" @click="previewCandidates">
                    <span v-if="currentReward.isLoadingPreview"><i class="ri-loader-4-line"></i> 検索中...</span>
                    <span v-else><i class="ri-search-line"></i> 検索</span>
                </button>
            </div>

            <!-- Preview result -->
            <div v-if="currentReward.candidateCount !== null" class="mt-3 alert alert-info">
                対象データ結果 件数 <strong>({{ currentReward.candidateCount }})</strong>
            </div>
            <div class="mt-2">
                <div class="d-flex justify-content-end mb-2">
                    <button
                        v-if="checkPermission('lottery.export')"
                        type="button"
                        class="btn btn-common btn-success"
                        :disabled="isExporting"
                        @click="doExport"
                    >
                        <span v-if="isExporting"><i class="ri-loader-4-line bx-spin"></i> ダウンロード中..</span>
                        <span v-else><i class="ri-file-download-line"></i> データダウンロード</span>
                    </button>
                </div>
                <div class="table-responsive">
                    <TableListComponent
                        :config="configCandidateTable"
                        :dataList="currentReward.candidateList"
                        :isLoading="currentReward.isLoadingPreview"
                        :configPage.sync="currentReward.candidatePage"
                        @onChangePage="onCandidatePageChange"
                    >
                        <template #row="{ row }">
                            <tr>
                                <td class="text-left">{{ row.formatted_index || row.row_index }}</td>
                                <td>
                                    <div class="el-table__cell text-left">
                                        {{ formatDateCommon(row.submitted_at || row.created_at, 'dateTimeJP') }}
                                    </div>
                                </td>
                                <td v-for="b in listBlockForm" :key="b.id">
                                    <div
                                        class="el-table__cell text-left"
                                        v-html="
                                            renderLineBreaks(
                                                row.row_data ? row.row_data[b.id] : getBlockValue(row.submit_results || row.blocks, b.id)
                                            )
                                        "
                                    ></div>
                                </td>
                                <td>
                                    <div class="el-table__cell text-left">
                                        {{ (row.tags || []).join(', ') }}
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </TableListComponent>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import TableFilterBlock from '@/views/pages/phase2/manager-form/analytics/components/manager-data/table-filter.vue';
import Multiselect from '@/components/Multiselect/main.vue';
import TableListComponent from '@/components/TableList/main.vue';
import { lotteryMethods, uploadMethods } from '@/state/helpers';
import { showMessage } from '@/utils/messages';
import { ALL_BLOCK_TYPE } from '@/config/constants';
import { checkPermission } from '@/utils/handlers';
import { formatDateCommon, renderLineBreaks } from '@/utils/format';

export default {
    components: { TableFilterBlock, Multiselect, TableListComponent },
    props: {
        lotteryId: { type: [Number, String], default: null },
        listBlockForm: { type: Array, default: () => [] },
        rewards: { type: Array, default: () => [] },
        isLoadingSetting: { type: Boolean, default: false }
    },
    data() {
        return {
            ALL_BLOCK_TYPE,
            editingRewardIdx: null,
            isAddingNew: false,
            currentReward: null,
            errors: {},
            isExporting: false,
            exportPollingTimer: null,
            configCandidateTable: {
                headers: [
                    { label: 'ID', width: 60 },
                    { label: '回答日時', width: 160 },
                    { label: '登録タグ', width: 200 }
                ]
            }
        };
    },
    watch: {
        editingRewardIdx(newVal) {
            this.$emit('update:is-editing', newVal !== null);
        },
        listBlockForm: {
            handler(newBlocks) {
                this.configCandidateTable = {
                    headers: [
                        { label: 'ID', width: 60 },
                        { label: '回答日時', width: 160 },
                        ...newBlocks.map((b) => ({ label: b.block_name, width: 200 })),
                        { label: '登録タグ', width: 200 }
                    ]
                };
            },
            immediate: true,
            deep: true
        }
    },
    computed: {},
    methods: {
        ...lotteryMethods,
        ...uploadMethods,
        checkPermission,
        formatDateCommon,
        renderLineBreaks,
        onQuantityKeypress(e) {
            const charCode = e.which ? e.which : e.keyCode;
            // Only allow digits (48-57). Block minus sign, dot, 'e', etc.
            if (charCode < 48 || charCode > 57) {
                e.preventDefault();
            }
        },
        onQuantityInput(e) {
            let val = String(e.target.value);
            if (val.length > 10) {
                val = val.slice(0, 10);
                e.target.value = val;
                this.currentReward.quantity = parseInt(val, 10) || 0;
            }
            this.validateQuantity();
        },
        validateText() {
            if (!this.currentReward.text) {
                this.$set(this.errors, 'text', '当選テキストを入力してください。');
            } else if (this.currentReward.text.length > 255) {
                this.$set(this.errors, 'text', '当選テキストは255文字以内で入力してください。');
            } else {
                this.$delete(this.errors, 'text');
            }
        },
        validateQuantity() {
            if (!this.currentReward.quantity || this.currentReward.quantity < 1) {
                this.$set(this.errors, 'quantity', '当選数を入力してください。');
            } else {
                this.$delete(this.errors, 'quantity');
            }
        },
        openAddReward() {
            this.errors = {};
            this.currentReward = {
                text: '',
                quantity: 1,
                conditionQuery: '',
                condition_config: { condition: 'AND', filter_groups: [] },
                itemBlocks: null,
                typeFilter: 'AND',
                preDrawFields: [null],
                postDrawFields: [null],
                fullDataDuplicateEnabled: false,
                candidateCount: null,
                candidateList: [],
                isLoadingPreview: false,
                candidatePage: { page: 1, limit: 10, total: 0 }
            };
            this.isAddingNew = true;
            this.editingRewardIdx = this.rewards.length;

            this.$nextTick(() => {
                if (this.$refs.tableFilter) {
                    this.$refs.tableFilter.ITEM_BLOCKS = [this.$refs.tableFilter.initVarTableFilter()];
                    this.$refs.tableFilter.typeFilter = 'AND';
                }
            });
        },
        editReward(idx) {
            this.errors = {};
            this.currentReward = JSON.parse(JSON.stringify(this.rewards[idx]));
            if (!this.currentReward.candidatePage) {
                this.currentReward.candidatePage = { page: 1, limit: 10, total: 0 };
            }
            this.isAddingNew = false;
            this.editingRewardIdx = idx;

            this.$nextTick(() => {
                if (this.$refs.tableFilter) {
                    if (this.currentReward.itemBlocks && this.currentReward.itemBlocks.length) {
                        this.$refs.tableFilter.ITEM_BLOCKS = JSON.parse(JSON.stringify(this.currentReward.itemBlocks));
                        this.$refs.tableFilter.typeFilter = this.currentReward.typeFilter || 'AND';
                    } else {
                        this.$refs.tableFilter.ITEM_BLOCKS = [this.$refs.tableFilter.initVarTableFilter()];
                        this.$refs.tableFilter.typeFilter = 'AND';
                    }
                    this.$refs.tableFilter.handleFilterResult();
                }
            });
        },
        deleteReward(idx) {
            Swal.fire({
                title: 'この賞品を削除してもよろしいですか？',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#556ee6',
                cancelButtonColor: '#e8ecf4',
                cancelButtonText: 'キャンセル',
                confirmButtonText: 'OK'
            }).then((result) => {
                if (result.value) {
                    const newRewards = [...this.rewards];
                    newRewards.splice(idx, 1);
                    this.$emit('update:rewards', newRewards);
                }
            });
        },
        cancelEditReward() {
            this.currentReward = null;
            this.editingRewardIdx = null;
        },
        saveEditReward() {
            this.errors = {};
            this.validateText();
            this.validateQuantity();
            if (Object.keys(this.errors).length > 0) return;
            if (this.$refs.tableFilter) {
                this.$refs.tableFilter.handleFilterResult();
                this.currentReward.itemBlocks = JSON.parse(JSON.stringify(this.$refs.tableFilter.ITEM_BLOCKS));
                this.currentReward.typeFilter = this.$refs.tableFilter.typeFilter;
            }

            this.currentReward.condition_config = this.parseConditionQuery(this.currentReward.conditionQuery);
            if (this.currentReward.typeFilter) {
                this.currentReward.condition_config.condition = this.currentReward.typeFilter;
            }

            const newRewards = [...this.rewards];
            if (this.isAddingNew) {
                newRewards.push(this.currentReward);
            } else {
                newRewards.splice(this.editingRewardIdx, 1, this.currentReward);
            }
            this.$emit('update:rewards', newRewards);

            this.currentReward = null;
            this.editingRewardIdx = null;
        },
        onConditionQuery(query) {
            if (this.currentReward) {
                this.currentReward.conditionQuery = query;
            }
        },
        parseConditionQuery(queryString) {
            if (!queryString) return { condition: 'AND', filter_groups: [] };

            let condition = 'AND';
            const params = new URLSearchParams(queryString);
            const groups = {};

            for (const [key, val] of params.entries()) {
                if (key === 'searchCriteria[filterGroups][condition]') {
                    condition = val;
                } else {
                    const match = key.match(/searchCriteria\[filterGroups\]\[(\d+)\]\[([^\]]+)\]\[(\d+)\]\[([^\]]+)\]/);
                    if (match) {
                        const groupIdx = parseInt(match[1]);
                        const arrayName = match[2];
                        const itemIdx = parseInt(match[3]);
                        const prop = match[4];

                        if (!groups[groupIdx]) groups[groupIdx] = {};
                        if (!groups[groupIdx][arrayName]) groups[groupIdx][arrayName] = [];
                        if (!groups[groupIdx][arrayName][itemIdx]) groups[groupIdx][arrayName][itemIdx] = {};

                        const finalVal = val === 'true' ? true : val === 'false' ? false : val;

                        if (key.endsWith('[]')) {
                            if (!groups[groupIdx][arrayName][itemIdx][prop]) {
                                groups[groupIdx][arrayName][itemIdx][prop] = [];
                            }
                            groups[groupIdx][arrayName][itemIdx][prop].push(finalVal);
                        } else {
                            groups[groupIdx][arrayName][itemIdx][prop] = finalVal;
                        }
                    }
                }
            }

            const filterGroupsArr = Object.keys(groups)
                .sort((a, b) => a - b)
                .map((k) => {
                    const group = groups[k];
                    const res = {};
                    if (group.filter_blocks) res.filter_blocks = group.filter_blocks.filter(Boolean);
                    if (group.filter_result) res.filter_result = group.filter_result.filter(Boolean);
                    return res;
                });

            return { condition, filter_groups: filterGroupsArr };
        },
        buildCandidateQuery(page = 1) {
            const limit = this.currentReward?.candidatePage?.limit || 10;
            let parts = [`include_list=true&page=${page}&limit=${limit}`];
            this.currentReward.preDrawFields.forEach((f, i) => {
                if (f?.id) parts.push(`pre_draw_duplicate_config[fields][${i}]=${f.id}`);
            });

            if (this.currentReward.conditionQuery) {
                parts.push(
                    this.currentReward.conditionQuery
                        .replace(/searchCriteria\[filterGroups\]\[condition\]=/g, 'condition_config[filter_groups][condition]=')
                        .replace(/searchCriteria\[filterGroups\]\[0\]\[filter_blocks\]/g, 'condition_config[filter_groups][0][filter_blocks]')
                        .replace(/searchCriteria\[filterGroups\]\[1\]\[filter_result\]/g, 'condition_config[filter_groups][1][filter_result]')
                );
            }
            return parts.join('&');
        },
        async onCandidatePageChange() {
            if (!this.lotteryId || !this.currentReward) return;
            const page = this.currentReward.candidatePage?.page || 1;
            this.currentReward.isLoadingPreview = true;
            try {
                const res = await this.candidatesPreview({ id: this.lotteryId, query: this.buildCandidateQuery(page) });
                this.currentReward.candidateCount = res?.data?.candidate_count ?? 0;
                this.currentReward.candidateList = res?.data?.candidates || res?.candidates || [];
                this.currentReward.candidatePage = {
                    ...this.currentReward.candidatePage,
                    total: this.currentReward.candidateCount
                };
            } catch (e) {
                console.error(e);
            } finally {
                this.currentReward.isLoadingPreview = false;
            }
        },
        async previewCandidates() {
            if (!this.lotteryId || !this.currentReward) return;

            // Force TableFilterBlock to emit its current query
            if (this.$refs.tableFilter) {
                this.$refs.tableFilter.handleFilterResult();
            }

            // Reset to page 1 on new preview
            this.currentReward.candidatePage = {
                ...this.currentReward.candidatePage,
                page: 1
            };
            this.currentReward.isLoadingPreview = true;
            this.currentReward.candidateCount = null;
            this.currentReward.candidateList = [];
            try {
                const res = await this.candidatesPreview({ id: this.lotteryId, query: this.buildCandidateQuery(1) });
                this.currentReward.candidateCount = res?.data?.candidate_count ?? 0;
                this.currentReward.candidateList = res?.data?.candidates || res?.candidates || [];
                this.currentReward.candidatePage = {
                    ...this.currentReward.candidatePage,
                    total: this.currentReward.candidateCount
                };
            } catch (e) {
                console.error(e);
            } finally {
                this.currentReward.isLoadingPreview = false;
            }
        },
        clearPreview() {
            if (!this.currentReward) return;
            this.currentReward.candidateList = [];
            this.currentReward.candidateCount = null;

            if (this.$refs.tableFilter) {
                this.$refs.tableFilter.handleClearSearch();
            }
            this.currentReward.preDrawFields = [null];
        },
        getBlockValue(blocks, blockId) {
            if (!blocks || !Array.isArray(blocks)) return '';
            const blockForm = this.listBlockForm.find((b) => b.id === blockId || b.id === String(blockId));
            if (!blockForm) return '';

            const result = blocks.find((item) => item.block_id === blockId || item.id === blockId || item.block_id === String(blockId));
            const resultValue = result?.answers?.[0];
            const listValueToString =
                result?.answers
                    ?.map((item) => {
                        return item.is_custom_value
                            ? `${blockForm.extension?.setting_others?.label_other || 'その他'}(${item.value ?? ''})`
                            : item.value ?? '';
                    })
                    ?.toString() ?? '';

            switch (blockForm.block_type) {
                case ALL_BLOCK_TYPE.BLOCK_UPLOAD_FILE:
                    return resultValue?.file?.file_name || resultValue?.file || '';
                case ALL_BLOCK_TYPE.BLOCK_RADIO:
                    if (resultValue?.is_custom_value) {
                        return `${blockForm.extension?.setting_others?.label_other || 'その他'}(${resultValue.value ?? ''})`;
                    } else {
                        return result ? (Array.isArray(resultValue.value) ? resultValue.value.join(', ') : resultValue.value) : '';
                    }
                case ALL_BLOCK_TYPE.BLOCK_CHECKBOX:
                    return listValueToString;
                default:
                    return result ? (Array.isArray(resultValue.value) ? resultValue.value.join(', ') : resultValue.value) : '';
            }
        },
        async doExport() {
            if (!this.lotteryId || !this.currentReward) return;
            if (this.$refs.tableFilter) {
                this.$refs.tableFilter.handleFilterResult();
            }
            this.isExporting = true;
            try {
                const res = await this.exportCandidates({ id: this.lotteryId, query: this.buildCandidateQuery() });
                const fileId = res?.data?.file_id || res?.file_id || res?.data?.id || res?.id;
                if (fileId) {
                    this.pollExportStatus(fileId);
                } else {
                    this.isExporting = false;
                    showMessage(500, this.$bvToast, 'ファイルのダウンロードに失敗しました (file_idなし).');
                }
            } catch (e) {
                this.isExporting = false;
                console.error('出力 Error:', e);
            }
        },
        pollExportStatus(fileId) {
            clearInterval(this.exportPollingTimer);
            this.exportPollingTimer = setInterval(async () => {
                try {
                    const res = await this.acgetJobStatusFile(fileId);
                    const status = res?.data?.status || res?.status;
                    if (status == 1 || status === 'completed') {
                        clearInterval(this.exportPollingTimer);
                        this.isExporting = false;
                        const fileUrl = res?.data?.path || res?.path;
                        if (fileUrl) {
                            window.location.href = fileUrl;
                        } else {
                            showMessage(500, this.$bvToast, 'ファイルのダウンロードに失敗しました (file_urlなし)');
                        }
                    } else if (status == 2 || status === 'failed') {
                        clearInterval(this.exportPollingTimer);
                        this.isExporting = false;
                        showMessage(500, this.$bvToast, 'ファイルのダウンロードに失敗しました (ステータス failed)');
                    }
                } catch (e) {
                    clearInterval(this.exportPollingTimer);
                    this.isExporting = false;
                    console.error('Poll Error:', e);
                }
            }, 3000);
        }
    },
    beforeDestroy() {
        if (this.exportPollingTimer) clearInterval(this.exportPollingTimer);
    }
};
</script>
